<script lang="ts" setup>
import type { SelectItem } from '@nuxt/ui';
import type { AtLeast } from '#layers/base/public';
import type { FieldFilter } from '#layers/data-model/types/filters';
import type { GlobalCollectionId, GlobalDataSourceId } from '#layers/data-model/types/schema/ids';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { MonospaceCollection, MonospaceSerializedCollection } from '#layers/data-model/types/schema/workspace-schema';
import type { SelectMenuProps } from '#layers/ui/public';
import { computed, watch } from 'vue';
import { useDataModelDisplayNames } from '#layers/data-model/composables/use-data-model-display-names';
import { useWorkspaceSchemaStore } from '#layers/data-model/stores/workspace-schema';
import { matchesFieldFilter } from '#layers/data-model/utils/filters';
import { mergeUi } from '#layers/ui/public';

type CreatedCollection = AtLeast<MonospaceSerializedCollection, 'id' | 'dbName' | 'apiName' | 'sourceId'>;

const {
	labelKey = 'displayName',
	clear = true,
	filter,
	createdCollection,
	autoSelectFirst,
	sourceId,
	includeSystem,
	whitelistSystem,
	showIcons,
	disableSchemaLocked,
} = defineProps<{
	sourceId?: GlobalDataSourceId;
	/**
	 * Include all system collections for the user to select from.
	 */
	includeSystem?: boolean;
	/**
	 * Whitelist a limited number of system collections that the user is allowed to select from.
	 */
	whitelistSystem?: readonly `Monospace${string}`[];
	/**
	 * Render the collection icons prefixed to the collection name
	 */
	showIcons?: boolean;
	/**
	 * Disable the collections of data sources with a locked schema
	 */
	disableSchemaLocked?: boolean;
	disabled?: boolean;
	labelKey?: 'apiName' | 'dbName' | 'displayName';
	filter?: FieldFilter<MonospaceCollection>;
	autoSelectFirst?: boolean;
	clear?: boolean;
	createdCollection?: CreatedCollection;
	ui?: SelectMenuProps['ui'];
}>();

const model = defineModel<GlobalCollectionId | null>();

const schemaStore = useWorkspaceSchemaStore();
const sourceNameResolver = computed(() => createSourceNameResolver());
const { getCollectionDisplayName, getDataSourceDisplayName } = useDataModelDisplayNames();

const filteredCollections = computed(() => {
	return schemaStore.collections
		.filter((c) => isSameSource(c))
		.filter((c) => isIncludedSystemCollection(c) || isSelected(c))
		.filter((c) => !filter || matchesFieldFilter(c, filter))
		.map((c) => makeCollectionItem(c))
		.toSorted((a, b) => a.label.localeCompare(b.label));
});

const createdCollectionItem = computed(() => createdCollection
	? makeCollectionItem(createdCollection, { isCreated: true })
	: undefined);

const items = computed(() => [
	createdCollectionItem.value,
	...filteredCollections.value,
].filter((item) => !!item));

const selectedItem = computed(() => items.value.find((item) => item.value === model.value));

watch([filteredCollections, model], () => {
	if (autoSelectFirst && filteredCollections.value.length > 0 && !model.value) {
		model.value = filteredCollections.value[0]!.value;
	}
}, { immediate: true });

const fontClasses = computed(() => labelKey === 'displayName' ? '' : 'font-medium font-mono');

function isSameSource(collection: MonospaceSerializedCollection) {
	return !sourceId || sourceId === collection.sourceId;
}

function isIncludedSystemCollection(collection: MonospaceSerializedCollection) {
	return includeSystem || !collection.isSystem || !!(whitelistSystem as string[])?.includes(collection.apiName);
}

function isSelected(collection: MonospaceSerializedCollection) {
	return model.value === collection.id;
}

function createSourceNameResolver() {
	const hasMoreThanOneSource = !sourceId && schemaStore.dataSources.length > 1;
	const sourceNameMap = Object.fromEntries(schemaStore.dataSources.map((source) => [source.id, getDataSourceDisplayName(source)]));

	return (sourceId: GlobalDataSourceId) => hasMoreThanOneSource ? sourceNameMap[sourceId] : undefined;
}

function makeCollectionItem(collection: AtLeast<ResolvedMonospaceCollection, 'id' | 'apiName' | 'dbName'>, options?: { isCreated?: boolean }) {
	const schemaLocked = disableSchemaLocked && !!collection.source?.schemaLocked;

	const selectItem = {
		value: collection.id,
		sourceName: collection.sourceId ? sourceNameResolver.value(collection.sourceId) : undefined,
		label: labelKey === 'displayName' ? getCollectionDisplayName(collection) : collection[labelKey],
		isCreated: options?.isCreated ?? false,
		schemaLocked,
		disabled: schemaLocked,
		...(showIcons ? { icon: collection.meta?.icon } : {}),
	} satisfies SelectItem;

	return selectItem;
}
</script>

<template>
	<DSelectMenu
		v-model="model as GlobalCollectionId"
		:placeholder="$t('collection_select.placeholder')"
		:items
		:disabled
		:clear
		spellcheck="false"
		value-key="value"
		:leading-icon="showIcons && selectedItem?.icon"
		:ui="mergeUi(ui, {
			base: [fontClasses, createdCollectionItem?.value === model ? 'font-medium text-primary' : ''],
			input: fontClasses,
			content: 'min-w-fit',
			itemLabel: 'flex flex-1 items-center justify-between gap-2',
		})"
	>
		<slot />

		<template #item-label="{ item }">
			<span :class="[fontClasses, item.isCreated ? 'text-primary' : '']">{{ item.label }}</span>
			<span class="flex items-center gap-2">
				<DBadge v-if="item.sourceName" color="neutral" variant="outline" size="sm">
					{{ item.sourceName }}
				</DBadge>
				<DTooltip v-if="item.schemaLocked" :text="$t('pages.data_model.schema_locked')">
					<DIcon name="lucide:lock" class="size-4 shrink-0" />
				</DTooltip>
			</span>
		</template>

		<!-- Forward only when given, so it can be specific to when there is no collections. -->
		<template v-if="$slots.empty" #empty>
			<slot name="empty" />
		</template>
	</DSelectMenu>
</template>
